import React, { useCallback, useEffect, useRef, useState } from "react"
import { usePopper } from "react-popper"
import useOnClickOutside from "../hooks/useOnClickOutside"
import getLocalizedString from "../utils/getLocalizedString"
import { imageItemTransformer } from "../utils/transformers"
import { useMainMenuItems } from "../utils/useMainMenuItems"
import Image from "./ImageOptim"
import Link from "./LinkWrapper"

type link = {
  id: string
  url: string
  label: string
  type: string
  links?: link[]
}

export interface MainNavigationProps {
  /**
   * Global content object used to get the main menu items
   */
  global: any
  /**
   * Current language of the website
   * @deprecated Language is now handled by the URL resolver
   */
  currentLanguage: string
  /**
   * An array of language-specific localized strings
   */
  localizedStrings: unknown[]
}

/**
 * The main navigation component
 */
const MainNavigation = ({
  global,
  currentLanguage,
  localizedStrings,
}: MainNavigationProps) => {
  const navItems = useMainMenuItems(global, currentLanguage)
  const [isOpen, setIsOpen] = useState<Boolean | number>()

  const handleKeyDown = useCallback((event: KeyboardEvent) => {
    switch (event.key) {
      case "Escape": // esc
        // Pressing esc should close the dropdown and restore focus to the top level item
        setIsOpen(false)
        break
      default:
        break
    }
  }, [])

  const handleOnClick = (
    event: React.MouseEvent<HTMLAnchorElement>,
    index: number
  ) => {
    event.preventDefault()

    setIsOpen((prevState) => {
      if (prevState === index) {
        return false
      } else {
        return index
      }
    })
  }

  useEffect(() => {
    document.addEventListener("keydown", handleKeyDown)

    return () => {
      document.removeEventListener("keydown", handleKeyDown)
    }
  }, [handleKeyDown])

  return (
    <ul className="flex h-full">
      {navItems.map((navItem, index) => {
        const _isOpen = isOpen === index

        return (
          <MainNavItem
            navItem={navItem}
            isOpen={_isOpen}
            key={navItem.id}
            onClick={(event: React.MouseEvent<HTMLAnchorElement>) =>
              handleOnClick(event, index)
            }
            onClickOutside={_isOpen ? () => setIsOpen(false) : () => {}}
            localizedStrings={localizedStrings}
          />
        )
      })}
    </ul>
  )
}

interface MainNavItemProps {
  /**
   * Whether the dropdown is open
   */
  isOpen: Boolean
  /**
   * The navigation item object
   */
  navItem: any
  /**
   * Function to handle clicking outside of the dropdown
   */
  onClickOutside: (ref: any, handler: any) => void
  /**
   * An array of language-specific localized strings
   */
  localizedStrings: any
  /**
   * Function to handle clicking in the dropdown
   */
  onClick: (event: React.MouseEvent<HTMLAnchorElement>) => void
}

/**
 * Top-level navigation item with dropdown
 */
export const MainNavItem = ({
  isOpen,
  navItem,
  onClick,
  onClickOutside,
  localizedStrings,
}: MainNavItemProps) => {
  const [referenceElement, setReferenceElement] = useState(null)
  const [popperElement, setPopperElement] = useState(null)
  const clickOutsideRef = useRef()

  useOnClickOutside(clickOutsideRef, onClickOutside)

  // Localized strings for nav footer
  const localizedProductsOverview = getLocalizedString(
    localizedStrings,
    "localization_string___products_overview",
    "Products Overview"
  )

  const localizedSolutionsOverview = getLocalizedString(
    localizedStrings,
    "localization_string___solutions_overview",
    "Solutions Overview"
  )

  const localizedIndustriesOverview = getLocalizedString(
    localizedStrings,
    "localization_string___industries_overview",
    "Industries Overview"
  )

  const localizedResourcesOverview = getLocalizedString(
    localizedStrings,
    "localization_string___resources_overview",
    "Resources Overview"
  )

  const localizedSupportOverview = getLocalizedString(
    localizedStrings,
    "localization_string___support_overview",
    "Support Overview"
  )

  const { styles: popperStyles, attributes: popperAttributes } = usePopper(
    referenceElement,
    popperElement,
    {
      strategy: "fixed",
    }
  )

  const section = {
    products__main_menu_group_: "products",
    services__main_menu_group_: "solutions",
    industries__main_menu_group_: "industries",
    resources__main_menu_group_: "resources",
    support__main_menu_group_: "support",
  }[navItem.codename]

  const localizedOverviewLabel =
    {
      products: localizedProductsOverview,
      solutions: localizedSolutionsOverview,
      industries: localizedIndustriesOverview,
      resources: localizedResourcesOverview,
      support: localizedSupportOverview,
    }[section] || navItem.label

  // Set styles for grid width based on the dropdown
  const dropdownWidth =
    {
      products: "sm",
      solutions: "sm",
      industries: "lg",
      resources: "md",
      support: "sm",
    }[section] || "sm"

  return (
    <li className={`h-full relative`} ref={clickOutsideRef} role="none">
      <Link
        className={`
          font-bold uppercase tracking-wide h-full inline-flex items-center px-4 border-l border-r
          ${
            isOpen
              ? `bg-gray-100 border-gray-300`
              : `hover:bg-gray-200 focus:bg-gray-200 border-transparent`
          }
          focus:outline-none focus:ring
        `}
        activeClassName={!isOpen ? `bg-gray-200` : ``}
        to={navItem.linkTo}
        partiallyActive={true}
        onClick={onClick}
        ref={setReferenceElement}
        aria-expanded={!!isOpen}
      >
        {navItem.label}
      </Link>
      <div
        className={`
          absolute z-10 top-0 left-0
          ${
            isOpen
              ? `visible pointer-events-auto`
              : `invisible pointer-events-none`
          }
        `}
        ref={setPopperElement}
        style={popperStyles.popper}
        {...popperAttributes.popper}
      >
        <Dropdown width={dropdownWidth}>
          <DropdownPane>
            {(() => {
              switch (section) {
                case "industries":
                  const industriesLinks = navItem?.components[0]?.links
                  return (
                    <div className="flex flex-wrap -ml-4 -mt-4">
                      {industriesLinks.map((link, index) => {
                        return (
                          <React.Fragment key={index}>
                            <div className="pl-4 w-1/3 mt-4">
                              <DropdownLinkGroup
                                to={link.url}
                                links={link.links}
                              >
                                {link.label}
                              </DropdownLinkGroup>
                            </div>
                            {/* Adds divider between rows (but not after the last row) */}
                            {(index + 1) % 3 === 0 &&
                            index + 1 !== industriesLinks.length ? (
                              <div className="pl-4 w-full mt-4">
                                <hr className="border-gray-300 border-solid border-0 border-t" />
                              </div>
                            ) : null}
                          </React.Fragment>
                        )
                      })}
                    </div>
                  )
                case "resources":
                  return (
                    <div className="flex flex-nowrap -ml-4">
                      <div className="pl-4 w-1/2">
                        <DropdownLinks
                          links={navItem?.components?.[1]?.links}
                        />
                      </div>
                      <div className="pl-4 w-1/2">
                        <DropdownPromo promo={navItem?.components?.[0]} />
                      </div>
                    </div>
                  )
                case "products":
                case "solutions":
                case "support":
                default:
                  return (
                    <DropdownLinks links={navItem?.components?.[0]?.links} />
                  )
              }
            })()}
          </DropdownPane>
          <DropdownFooter>
            <div
              className={`leading-tight ${
                section === "industries" ? `w-1/3 pr-3` : ``
              } ${section === "resources" ? `w-1/2 pr-2` : ``}`}
            >
              <DropdownMoreLink activeClassName="" to={navItem.linkTo}>
                {localizedOverviewLabel}
              </DropdownMoreLink>
            </div>
          </DropdownFooter>
        </Dropdown>
      </div>
    </li>
  )
}

export interface DropdownLinksProps {
  /**
   * Array of links to display
   */
  links: link[]
}

/**
 * Displays a list of links with optional nested links
 */
export const DropdownLinks = ({ links = [] }: DropdownLinksProps) => {
  return (
    <ul className="space-y-1px leading-tight">
      {links.map((link, index) => {
        const childLinks = link.links || []
        return (
          <React.Fragment key={index}>
            <li>
              <DropdownMoreLink to={link.url}>{link.label}</DropdownMoreLink>
              {childLinks.length ? (
                <DropdownMutedLinks links={childLinks} />
              ) : null}
            </li>
            {/* Adds divider between child links and the next link or link group (but not if the last item) */}
            {childLinks.length && index + 1 !== links.length ? (
              <li className="px-1" aria-hidden="true">
                <hr className="border-gray-300 border-solid border-0 border-t my-2" />
              </li>
            ) : (
              ``
            )}
          </React.Fragment>
        )
      })}
    </ul>
  )
}

interface DropdownProps {
  /**
   * Content to display in the dropdown
   */
  children: React.ReactNode
  /**
   * Width of the dropdown
   */
  width: "sm" | "md" | "lg"
}

/**
 * Styled container for dropdown content
 */
export const Dropdown = ({ children, width = "sm" }: DropdownProps) => {
  let _width

  switch (width) {
    case "lg":
      _width = "w-screen-md"
      break
    case "md":
      _width = "w-screen-sm"
      break
    case "sm":
    default:
      _width = "w-56"
      break
  }

  return (
    <div
      className={`${_width} rounded-lg shadow overflow-hidden bg-white border border-gray-300`}
    >
      {children}
    </div>
  )
}

export interface DropdownFooterProps {
  children: React.ReactNode
}

export const DropdownFooter = ({ children }: DropdownFooterProps) => {
  return (
    <div className="px-4 py-2 bg-gray-100 border-t border-gray-300">
      {children}
    </div>
  )
}

export interface DropdownPaneProps {
  children: React.ReactNode
}

export const DropdownPane = ({ children }: DropdownPaneProps) => {
  return <div className="p-4">{children}</div>
}

export interface DropdownMoreLinkProps {
  /**
   * The content to display in the link, typically a label
   */
  children: React.ReactNode
  /**
   * The URL you want to link to
   */
  to: string
  /**
   * The class to apply when the link is active
   */
  activeClassName?: string
  /**
   * Additional classes to apply to the link
   */
  className?: string
}

/**
 * A link styled to appear prominently in a dropdown
 */
export const DropdownMoreLink = ({
  activeClassName = "bg-gray-200 bg-black text-gray-700",
  children,
  className = "",
  to,
}: DropdownMoreLinkProps) => {
  return (
    <Link
      to={to}
      className={`
        block p-1 bg-transparent hover:bg-gray-200 focus:bg-gray-200 focus:outline-none focus:ring text-gray-900 text-sm font-medium rounded transition-colors duration-150
        ${className}
      `}
      activeClassName={activeClassName}
    >
      {children}
      <i
        className={`icon-chevron-right text-gray-400 inline-block`}
        /* Prevents icon wrapping to new line */
        style={{ marginRight: "-1em" }}
      />
    </Link>
  )
}

interface DropdownLinkGroup {
  /**
   * The content for the top-level item in the group, typically a label
   */
  children: React.ReactNode
  /**
   * The URL to the top-level item for the group
   */
  to: string
  /**
   * Links to display in the group
   */
  links: link[]
}

/**
 * A list of links nested under a more prominently styled top-level item
 */
export const DropdownLinkGroup = ({ children, to, links = [] }) => {
  return (
    <ul className="leading-tight space-y-1px">
      <DropdownMoreLink to={to}>{children}</DropdownMoreLink>
      {links.length ? <DropdownMutedLinks links={links} /> : null}
    </ul>
  )
}

interface DropdownMutedLinksProps {
  /**
   * Links to display
   */
  links: link[]
  /**
   * Additional classes to apply to the list
   */
  className?: string
}

/**
 * A list of links with muted styling
 */
export const DropdownMutedLinks = ({
  links,
  className = ``,
}: DropdownMutedLinksProps) => {
  return (
    <ul className={`${className} space-y-1px`}>
      {links.map((link) => {
        return (
          <li key={link.id}>
            <DropdownLink to={link.url}>{link.label}</DropdownLink>
          </li>
        )
      })}
    </ul>
  )
}

interface DropdownLinkProps {
  /**
   * The content to display in the link, typically a label
   */
  children: React.ReactNode
  /**
   * The URL you want to link to
   */
  to: string
}

/**
 * Basic link style for dropdowns
 */
export const DropdownLink = ({ children, to }: DropdownLinkProps) => {
  const internal = /^\/(?!\/)/.test(to)
  const external = !internal

  return (
    <Link
      to={to}
      className={`p-1 block text-sm text-gray-700 rounded transition-colors duration-150 hover:bg-gray-200 focus:bg-gray-200 focus:outline-none focus:ring`}
      activeClassName={`bg-gray-300 text-gray-900`} /* TODO: use prop to determine if active */
      partiallyActive={true}
      target={external ? "_blank" : undefined}
    >
      {children}
    </Link>
  )
}

interface DropdownPromoProps {
  /**
   * Data from the promo content item to display
   */
  promo: any
}

/**
 * A promo item to display in a dropdown
 */
export const DropdownPromo = ({ promo }: DropdownPromoProps) => {
  const heading = promo.elements.heading.value
  const description = promo.elements.description.value
  const [image] = promo.elements.image.value
  const [linkEntry] = promo.elements.link__link_to.value
  const linkTo = linkEntry ? linkEntry.resolved_url : ``

  return (
    <Link
      to={linkTo}
      className="space-y-1 block group hover:bg-gray-200 focus:bg-gray-200 focus:outline-none focus:ring transition-colors duration-150 rounded p-1 -mr-1 -mt-1"
    >
      {image ? (
        <Image
          cropRatio={0.333}
          cropWidth={283}
          {...imageItemTransformer(image)}
        />
      ) : null}
      <div>
        <p
          className={`
          text-action-link group-hover:text-action group-focus:text-action text-sm font-medium transition-colors duration-150
          `}
        >
          {heading}
          <i
            className={`icon-chevron-right text-gray-400 inline-block`}
            style={{ marginRight: "-1em" }}
          />
        </p>
        {description && (
          <p className={`text-xs leading-tight text-gray-700`}>{description}</p>
        )}
      </div>
    </Link>
  )
}

export { MainNavigation }
